import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import resolveExcelEditConfig from "../../../../Controls/ExcelWebEdit/excelEditConfig";
import { toColumnLabel } from "../utils/columnUtils";
import evaluateFormula, { isFormula } from "../utils/formulaEngine";
import { patchWorkbookCells } from "../utils/xlsxPatcher";

/**
 * useCellEditing — in-place cell editing for SimpleExcelViewer.
 *
 * Opt-in per reader email via AppContext.ExcelWebEdit (same config the
 * standalone editor used). Edits are committed from the formula bar,
 * applied to the display model immediately, and saved to the server by
 * patching ONLY the changed cells' XML inside the original .xlsx (via
 * xlsxPatcher/JSZip) so charts, drawings, images, styles and every other
 * part survive byte-for-byte, then POSTing the result to the save endpoint.
 *
 * Editing is blocked while a sort or filter is active: those reorder the
 * DISPLAY rows, so a display-coordinate edit could land on the wrong
 * file row.
 */
export default function useCellEditing({
  userId, documentId, openId, fileName, fileType,
  activeSheet, sheetDataByName, setSheetDataByName, workbookBufferRef,
  sortActive, filterEnabled,
  applySheetRename, // (oldName, newName) => void — used to revert renames on cancel
}) {
  const config = useMemo(() => resolveExcelEditConfig(userId), [userId]);

  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  // True right after a successful save - the viewer shows a success popup.
  const [saveSuccess, setSaveSuccess] = useState(false);
  // { [sheetName]: { "row:col": newValue } } — display coords, 0-based.
  const editedCellsRef = useRef({});
  // User-entered formula cells per sheet: { [sheet]: Set("row:col") }.
  // Recomputed live on every edit so dependents update like Excel.
  const formulaCellsRef = useRef({});
  // Undo stack: one entry per cell-edit "burst" (live keystrokes into the
  // same cell coalesce into a single entry, like Excel's undo). Entries
  // carry a batch id so one Ctrl+Z reverts a whole multi-cell paste.
  const undoStackRef = useRef([]);
  const undoBatchRef = useRef(0);
  // Guard so an undo application doesn't push onto the undo stack itself.
  const isUndoingRef = useRef(false);
  // Pending sheet renames: { [currentName]: originalName } (unsaved).
  const renameOriginalsRef = useRef({});
  // Keep the latest rename applier in a ref (stable effect deps).
  const applySheetRenameRef = useRef(applySheetRename);
  applySheetRenameRef.current = applySheetRename;

  const hasPendingRenames = useCallback(() =>
    Object.keys(renameOriginalsRef.current).some(
      (cur) => renameOriginalsRef.current[cur] !== cur
    ), []);

  // The patcher works on OOXML (.xlsx/.xlsm) only; legacy binary .xls stays
  // view-only.
  const ext = String(fileType || "").toLowerCase();
  const formatEditable = ext !== "xls";
  const canEdit = !!(config && config.canEdit === true) && formatEditable;
  const canSave = canEdit && !!(config && config.canSave === true);
  const editBlocked = !!(sortActive || filterEnabled);

  const toggleEditMode = useCallback(() => setEditMode((m) => !m), []);

  // Cancel editing: revert every UNSAVED edit back to its original value
  // (edits already saved to the server are untouched) and leave edit mode.
  const cancelEdits = useCallback(() => {
    Object.entries(editedCellsRef.current).forEach(([sheetName, cells]) => {
      setSheetDataByName((prev) => {
        const model = prev[sheetName];
        if (!model) return prev;
        const rows = (model.rows || []).slice();
        Object.entries(cells).forEach(([key, entry]) => {
          const [r, c] = key.split(":").map(Number);
          if (!rows[r]) return;
          rows[r] = rows[r].slice();
          const s = entry.oldValue;
          const num = Number(s);
          rows[r][c] = s !== "" && String(s).trim() !== "" && !isNaN(num) && isFinite(num) ? num : s;
        });
        return { ...prev, [sheetName]: { ...model, rows } };
      });
    });
    // Revert any unsaved sheet renames back to their original names.
    Object.entries(renameOriginalsRef.current).forEach(([current, original]) => {
      if (current !== original && typeof applySheetRenameRef.current === "function") {
        applySheetRenameRef.current(current, original);
      }
    });
    renameOriginalsRef.current = {};
    editedCellsRef.current = {};
    formulaCellsRef.current = {};
    undoStackRef.current = [];
    setDirty(false);
    setStatusMsg("");
    setEditMode(false);
  }, [setSheetDataByName]);

  // Batch commit: apply several cell edits against ONE working copy of the
  // sheet (multi-cell paste needs this — repeated single commits within the
  // same tick would each start from the stale model and clobber each other).
  const commitCells = useCallback((cellEdits) => {
    if (!canEdit || !activeSheet || !Array.isArray(cellEdits) || cellEdits.length === 0) return;
    const model = sheetDataByName[activeSheet];
    if (!model) return;

    const fset = formulaCellsRef.current[activeSheet];
    const fileFormulaCount = Object.keys(model.formulas || {}).length;
    // Clone every row whenever ANY formula may recompute (user or file) or
    // when the batch touches multiple rows. Otherwise clone only the touched
    // row — the fast path that keeps the grid's per-row memoization intact.
    const deepClone =
      cellEdits.length > 1 ||
      cellEdits.some((e2) => isFormula(String(e2.text))) ||
      (fset && fset.size > 0) ||
      fileFormulaCount > 0;
    const rows = deepClone
      ? (model.rows || []).map((r) => (r ? r.slice() : []))
      : (model.rows || []).slice();

    const richHtml = { ...(model.richHtml || {}) };
    const formulas = { ...(model.formulas || {}) };

    // Live resolver reads the working rows (so dependent formulas see the
    // freshly-typed values).
    const getCell = (r, c) => {
      const v = rows[r] && rows[r][c];
      return v == null ? "" : v;
    };

    let anyChange = false;
    const undoBatch = ++undoBatchRef.current;

    cellEdits.forEach(({ row, col, text }) => {
      if (row == null || col == null) return;
      const key = `${row}:${col}`;
      const entered = String(text);
      const isF = isFormula(entered);
      while (rows.length <= row) rows.push([]);
      if (!deepClone) rows[row] = (rows[row] || []).slice();
      if (!rows[row]) rows[row] = [];

      const oldVal = rows[row][col];
      const prevFormula = formulas[key] || null;

      let storedValue;
      if (isF) {
        formulas[key] = entered.trim();
        if (!formulaCellsRef.current[activeSheet]) formulaCellsRef.current[activeSheet] = new Set();
        formulaCellsRef.current[activeSheet].add(key);
        storedValue = evaluateFormula(entered, getCell);
      } else {
        delete formulas[key];
        if (fset) fset.delete(key);
        const num = Number(entered);
        storedValue =
          entered !== "" && entered.trim() !== "" && !isNaN(num) && isFinite(num) ? num : entered;
        // Nothing changed on a plain value edit -> skip (formulas always proceed).
        if (String(oldVal == null ? "" : oldVal) === String(storedValue)) return;
      }

      // Undo bookkeeping: one entry per cell-edit burst — consecutive live
      // keystrokes into the same cell keep the FIRST entry (whose prev value
      // predates the burst), exactly like Excel's whole-edit undo.
      if (!isUndoingRef.current) {
        const stack = undoStackRef.current;
        const top = stack[stack.length - 1];
        if (!(top && top.sheet === activeSheet && top.row === row && top.col === col)) {
          stack.push({
            sheet: activeSheet, row, col,
            prevValue: oldVal == null ? "" : oldVal,
            prevFormula,
            batch: undoBatch,
          });
          if (stack.length > 200) stack.shift();
        }
      }

      rows[row][col] = storedValue;
      delete richHtml[key];
      anyChange = true;

      const bySheet =
        editedCellsRef.current[activeSheet] || (editedCellsRef.current[activeSheet] = {});
      const prevEntry = bySheet[key];
      const trueOld = prevEntry ? prevEntry.oldValue : String(oldVal == null ? "" : oldVal);
      // If a plain-value edit lands back on the cell's ORIGINAL value (e.g.
      // Escape reverts live edits, or the user retypes the original), drop the
      // entry entirely so it doesn't count as an unsaved change or log a
      // no-op in history.
      if (!isF && String(storedValue) === String(trueOld)) {
        delete bySheet[key];
      } else {
        bySheet[key] = {
          value: storedValue,
          formula: isF ? entered.trim() : null,
          oldValue: trueOld,
        };
      }
    });

    if (!anyChange) return;

    // Recompute every USER formula cell on this sheet so dependents update
    // live.
    const userFormulas = formulaCellsRef.current[activeSheet];
    if (userFormulas && userFormulas.size > 0) {
      userFormulas.forEach((fk) => {
        const f = formulas[fk];
        if (!f) return;
        const [fr, fc] = fk.split(":").map(Number);
        if (!rows[fr]) rows[fr] = [];
        const res = evaluateFormula(f, getCell);
        rows[fr][fc] = res;
        const eb = editedCellsRef.current[activeSheet];
        if (eb && eb[fk]) eb[fk].value = res;
      });
    }

    // Recompute the FILE's own formulas too, so editing a referenced cell
    // updates dependents live (QA: "formulas are not working"). Formulas the
    // engine can't evaluate (unsupported functions, cross-sheet refs) keep
    // their cached Excel result instead of showing an error.
    if (fileFormulaCount > 0) {
      Object.keys(formulas).forEach((fk) => {
        if (userFormulas && userFormulas.has(fk)) return; // already recomputed
        const res = evaluateFormula(formulas[fk], getCell);
        if (typeof res === "string" && res[0] === "#") return; // keep cached value
        const [fr, fc] = fk.split(":").map(Number);
        if (!rows[fr]) rows[fr] = [];
        if (String(rows[fr][fc] == null ? "" : rows[fr][fc]) === String(res)) return;
        rows[fr][fc] = res;
        delete richHtml[fk]; // cached rich text would mask the new value
      });
    }

    setSheetDataByName((prev) => ({
      ...prev,
      [activeSheet]: { ...(prev[activeSheet] || model), rows, richHtml, formulas },
    }));
    // Dirty only while at least one edit (or sheet rename) remains.
    const anyEdits = Object.values(editedCellsRef.current).some(
      (s) => s && Object.keys(s).length > 0
    );
    setDirty(anyEdits || hasPendingRenames());
  }, [canEdit, activeSheet, sheetDataByName, setSheetDataByName, hasPendingRenames]);

  const commitCell = useCallback(
    (row, col, text) => commitCells([{ row, col, text }]),
    [commitCells]
  );

  // Undo (Ctrl+Z): revert the most recent cell-edit burst on the ACTIVE
  // sheet back to the value (or formula) it held before that burst. All
  // entries sharing the last batch id (a multi-cell paste) revert together.
  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    let last = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].sheet === activeSheet) { last = i; break; }
    }
    if (last === -1) return;
    const batch = stack[last].batch;
    const entries = [];
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].sheet === activeSheet && stack[i].batch === batch) {
        entries.push(stack.splice(i, 1)[0]);
      }
    }
    const edits = entries.map((entry) => {
      let text;
      if (entry.prevFormula) {
        const f = String(entry.prevFormula);
        text = f.trim().startsWith("=") ? f : `=${f}`;
      } else {
        text = String(entry.prevValue == null ? "" : entry.prevValue);
      }
      return { row: entry.row, col: entry.col, text };
    });
    isUndoingRef.current = true;
    try {
      commitCells(edits);
    } finally {
      isUndoingRef.current = false;
    }
  }, [activeSheet, commitCells]);

  // Ctrl+Z / Cmd+Z while edit mode is on (outside text fields, which keep
  // their native input undo).
  useEffect(() => {
    if (!editMode || !canEdit) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if (String(e.key).toLowerCase() !== "z") return;
      const t = e.target;
      const tag = String(t.tagName || "").toLowerCase();
      // Text fields keep their native undo — EXCEPT the viewer's own
      // formula-bar input, which forwards Ctrl+Z here when it has no
      // pending draft (data-sev-undo-passthrough).
      if (
        (tag === "input" || tag === "textarea") &&
        !(t.dataset && t.dataset.sevUndoPassthrough === "true")
      ) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editMode, canEdit, undo]);

  // Record a sheet rename so it (a) survives per-sheet edit bookkeeping and
  // (b) is persisted into workbook.xml on save.
  const registerSheetRename = useCallback((oldName, newName) => {
    const ec = editedCellsRef.current;
    if (ec[oldName]) { ec[newName] = ec[oldName]; delete ec[oldName]; }
    const fc = formulaCellsRef.current;
    if (fc[oldName]) { fc[newName] = fc[oldName]; delete fc[oldName]; }
    undoStackRef.current.forEach((en) => { if (en.sheet === oldName) en.sheet = newName; });

    const originals = renameOriginalsRef.current;
    const orig = originals[oldName] || oldName;
    delete originals[oldName];
    if (orig !== newName) originals[newName] = orig;

    const anyEdits = Object.values(ec).some((s) => s && Object.keys(s).length > 0);
    setDirty(anyEdits || hasPendingRenames());
  }, [hasPendingRenames]);

  const save = useCallback(async () => {
    if (!canSave || saving || !dirty) return;
    if (!workbookBufferRef.current) {
      setStatusMsg("Original workbook unavailable");
      return;
    }
    try {
      setSaving(true);
      setStatusMsg("");

      // Patch ONLY the edited cells inside the original .xlsx, preserving
      // charts, drawings, images, styles and every other part byte-for-byte.
      // (ExcelJS was used here before, but it silently drops charts/drawings
      // when it re-writes the workbook.)
      // { originalName: currentName } for every pending rename.
      const sheetRenames = {};
      Object.entries(renameOriginalsRef.current).forEach(([current, original]) => {
        if (current !== original) sheetRenames[original] = current;
      });

      const outBytes = await patchWorkbookCells(
        workbookBufferRef.current.slice(0),
        editedCellsRef.current,
        sheetRenames
      );
      let bin = "";
      const CHUNK = 0x8000;
      for (let i = 0; i < outBytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, outBytes.subarray(i, i + CHUNK));
      }

      // Per-cell change list for the server-side edit-history log.
      const edits = [];
      Object.entries(editedCellsRef.current).forEach(([sheetName, cells]) => {
        Object.entries(cells).forEach(([key, entry]) => {
          const [r, c] = key.split(":").map(Number);
          edits.push({
            sheet: sheetName,
            row: r,
            col: c,
            cellRef: `${toColumnLabel(c)}${r + 1}`,
            oldValue: entry.oldValue,
            // Show the formula in history when the cell holds one.
            newValue: entry.formula
              ? `${entry.formula} = ${entry.value == null ? "" : entry.value}`
              : String(entry.value == null ? "" : entry.value),
          });
        });
      });

      const url = `${process.env.REACT_APP_API}${config.__savePath}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          documentId,
          id: openId,
          fileName: fileName || "",
          fileExtension: "xlsx",
          fileContentBase64: btoa(bin),
          edits,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);

      // Future edits stack on the patched workbook (charts still intact).
      workbookBufferRef.current = outBytes.buffer.slice(
        outBytes.byteOffset,
        outBytes.byteOffset + outBytes.byteLength
      );
      editedCellsRef.current = {};
      renameOriginalsRef.current = {};
      undoStackRef.current = [];
      setDirty(false);
      setSaveSuccess(true);
      // Leave edit mode after a successful save so the toolbar shows the
      // Edit icon again (QA #8).
      setEditMode(false);
      setTimeout(() => setSaveSuccess(false), 6000);
    } catch (e) {
      console.error("[ExcelWebEdit] save error", e);
      setStatusMsg(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [canSave, saving, dirty, config, documentId, openId, fileName, workbookBufferRef]);

  // Fetch the full edit history for this document's spreadsheet (the
  // caller filters per cell). Available to any configured reader.
  const fetchHistory = useCallback(async () => {
    if (!config) return [];
    // Pass the current spreadsheet's fileName so multi-file documents return
    // the history for THIS file, not the first Excel input.
    const q = fileName ? `?fileName=${encodeURIComponent(fileName)}` : "";
    const url = `${process.env.REACT_APP_API}${config.__downloadPath}/${documentId}/${openId}/history${q}`;
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) throw new Error(`History request failed (${res.status})`);
    const data = await res.json();
    return Array.isArray(data.entries) ? data.entries : [];
  }, [config, documentId, openId, fileName]);

  return {
    canEdit,
    canSave,
    editMode: editMode && canEdit,
    toggleEditMode,
    editBlocked,
    commitCell,
    commitCells,
    undo,
    registerSheetRename,
    save,
    dirty,
    saving,
    statusMsg,
    historyEnabled: !!config,
    fetchHistory,
    cancelEdits,
    saveSuccess,
    clearSaveSuccess: () => setSaveSuccess(false),
  };
}
